import { useState, useEffect, useRef, useCallback } from 'react';
import {
  X, Bot, Settings, Trash2, Send, ChevronDown, ChevronUp,
  CheckCircle, AlertCircle, Loader2, Eye, EyeOff,
} from 'lucide-react';
import useFlowStore from '../../stores/useFlowStore';
import { aiApi } from '../../utils/api';
import { buildContextSchema } from '../../utils/promptBuilder';
import { parseAiResponse } from '../../utils/aiResponseParser';
import { useConfirm } from '../../hooks/useConfirm';
import ConfirmDialog from './ConfirmDialog';
import toast from 'react-hot-toast';

const PROVIDERS = [
  {
    value: 'openai', label: 'OpenAI',
    needsUrl: false, needsOllamaAuth: false,
    models: ['gpt-4o', 'gpt-4o-mini', 'gpt-4-turbo', 'gpt-3.5-turbo'],
  },
  {
    value: 'gemini', label: 'Google Gemini',
    needsUrl: false, needsOllamaAuth: false,
    models: ['gemini-2.0-flash', 'gemini-1.5-pro', 'gemini-1.5-flash'],
  },
  {
    value: 'groq', label: 'Groq Cloud',
    needsUrl: false, needsOllamaAuth: false,
    models: ['llama-3.3-70b-versatile', 'llama-3.1-8b-instant', 'deepseek-r1-distill-llama-70b', 'mixtral-8x7b-32768'],
  },
  {
    value: 'mistral', label: 'Mistral AI',
    needsUrl: false, needsOllamaAuth: false,
    models: ['mistral-small-latest', 'codestral-latest', 'pixtral-12b', 'open-mistral-nemo'],
  },
  {
    value: 'anthropic', label: 'Anthropic',
    needsUrl: false, needsOllamaAuth: false,
    models: ['claude-3-5-sonnet-20241022', 'claude-3-opus-20240229', 'claude-3-haiku-20240307'],
  },
  {
    value: 'ollama', label: 'Ollama (Cloud)',
    needsUrl: true, needsOllamaAuth: true,
    models: ['llama3', 'llama3.1', 'mistral', 'phi3', 'gemma2'],
  },
  {
    value: 'custom', label: 'Custom (OpenAI-compatible)',
    needsUrl: true, needsOllamaAuth: false,
    models: [],
  },
];

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function ConfigForm({ currentSettings, onSaved, onCancel }) {
  const [form, setForm] = useState({
    provider:       currentSettings?.provider       || 'openai',
    model:          currentSettings?.model          || 'gpt-4o-mini',
    baseUrl:        currentSettings?.baseUrl        || '',
    apiKey:         '',
    ollamaUser:     '',
    ollamaPassword: '',
  });
  const [showKey, setShowKey]   = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [saving, setSaving]     = useState(false);

  // Sync form state whenever currentSettings loaded from API updates
  useEffect(() => {
    if (currentSettings) {
      console.log('[AI Assistant] Syncing ConfigForm with currentSettings:', currentSettings);
      setForm(prev => {
        const pInfo = PROVIDERS.find(p => p.value === (currentSettings.provider || prev.provider)) || PROVIDERS[0];
        return {
          ...prev,
          provider: currentSettings.provider || prev.provider || 'openai',
          model: currentSettings.model || pInfo?.models[0] || prev.model || 'gpt-4o-mini',
          baseUrl: currentSettings.baseUrl || prev.baseUrl || '',
        };
      });
    }
  }, [currentSettings]);

  const providerInfo    = PROVIDERS.find(p => p.value === form.provider) || PROVIDERS[0];
  const isOllama        = form.provider === 'ollama';
  const isCustom        = form.provider === 'custom';
  const needsUrl        = !!providerInfo?.needsUrl;
  const needsOllamaAuth = !!providerInfo?.needsOllamaAuth;

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleProviderChange = (newProvider) => {
    const pInfo = PROVIDERS.find(p => p.value === newProvider) || PROVIDERS[0];
    const defaultModel = pInfo?.models[0] || '';
    setForm(prev => ({
      ...prev,
      provider: newProvider,
      model: defaultModel || prev.model,
      baseUrl: (newProvider === 'openai' || newProvider === 'gemini' || newProvider === 'mistral' || newProvider === 'anthropic') ? '' : prev.baseUrl
    }));
  };

  const handleSave = async () => {
    console.log('[AI Assistant] Save requested with form:', form);
    if (!form.model.trim()) { toast.error('Model name is required.'); return; }
    if (needsUrl && !form.baseUrl.trim()) { toast.error('URL base is required for this provider.'); return; }
    if (needsOllamaAuth && (!form.ollamaUser.trim() || !form.ollamaPassword.trim())) {
      toast.error('Username and password are required for Ollama.'); return;
    }
    if (!needsOllamaAuth && !form.apiKey.trim() && !currentSettings?.hasCredentials) {
      toast.error('API key is required.'); return;
    }

    // If editing and key is empty, do not send it (keep existing)
    const payload = {
      provider: form.provider,
      model:    form.model.trim(),
      baseUrl:  form.baseUrl.trim() || null,
    };
    if (needsOllamaAuth) {
      payload.ollamaUser     = form.ollamaUser.trim();
      payload.ollamaPassword = form.ollamaPassword.trim();
    } else if (form.apiKey.trim()) {
      payload.apiKey = form.apiKey.trim();
    }

    setSaving(true);
    try {
      console.log('[AI Assistant] Posting save payload:', payload);
      const res = await aiApi.saveSettings(payload);
      console.log('[AI Assistant] Save response:', res);
      toast.success('AI settings saved successfully.');
      onSaved({ provider: form.provider, model: form.model.trim(), baseUrl: form.baseUrl.trim() || null, hasCredentials: true });
    } catch (err) {
      console.error('[AI Assistant] Error saving settings:', err);
      toast.error(err.response?.data?.message || err.message || 'Error saving settings.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600 dark:text-gray-400">
        Configure your AI provider. Credentials are stored encrypted on the server and never sent back to the browser.
      </p>

      {/* Provider */}
      <div>
        <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase mb-1">Provider</label>
        <select
          value={form.provider}
          onChange={e => handleProviderChange(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
        >
          {PROVIDERS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
        </select>
      </div>

      {/* Model */}
      <div>
        <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase mb-1">Model</label>
        <input
          type="text"
          value={form.model}
          onChange={e => set('model', e.target.value)}
          placeholder={providerInfo.models[0] || 'model-name'}
          list="ai-model-suggestions"
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
        />
        {providerInfo.models.length > 0 && (
          <datalist id="ai-model-suggestions">
            {providerInfo.models.map(m => <option key={m} value={m} />)}
          </datalist>
        )}
      </div>

      {/* Base URL */}
      {needsUrl && (
        <div>
          <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase mb-1">
            Base URL <span className="text-red-500">*</span>
          </label>
          <input
            type="url"
            value={form.baseUrl}
            onChange={e => set('baseUrl', e.target.value)}
            placeholder={isOllama ? 'https://your-ollama-server.com' : 'https://api.groq.com/openai/v1'}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
          />
          {isCustom && (
            <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
              Include <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">/v1</code> if the provider requires it.
              The path <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">/chat/completions</code> is appended automatically.
              Ex: Groq → <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">https://api.groq.com/openai/v1</code>
            </p>
          )}
        </div>
      )}

      {/* API Key (non-ollama) */}
      {!needsOllamaAuth && (
        <div>
          <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase mb-1">
            API Key {!currentSettings?.hasCredentials && <span className="text-red-500">*</span>}
          </label>
          <div className="relative">
            <input
              type={showKey ? 'text' : 'password'}
              value={form.apiKey}
              onChange={e => set('apiKey', e.target.value)}
              placeholder={currentSettings?.hasCredentials ? '(keep existing)' : 'sk-...'}
              className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
            />
            <button type="button" onClick={() => setShowKey(v => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
              {showKey ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </div>
      )}

      {/* Ollama credentials */}
      {needsOllamaAuth && (
        <>
          <div>
            <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase mb-1">Username <span className="text-red-500">*</span></label>
            <input
              type="text"
              value={form.ollamaUser}
              onChange={e => set('ollamaUser', e.target.value)}
              placeholder="your-username"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase mb-1">Password <span className="text-red-500">*</span></label>
            <div className="relative">
              <input
                type={showPass ? 'text' : 'password'}
                value={form.ollamaPassword}
                onChange={e => set('ollamaPassword', e.target.value)}
                placeholder={currentSettings?.hasCredentials ? '(keep existing)' : ''}
                className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
              />
              <button type="button" onClick={() => setShowPass(v => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
        </>
      )}

      <div className="flex gap-2 pt-2">
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium transition-colors"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : null}
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
        {onCancel && (
          <button onClick={onCancel}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
            Cancel
          </button>
        )}
      </div>
    </div>
  );
}

function ChangeSummary({ changesJson }) {
  const [open, setOpen] = useState(false);
  if (!changesJson) return null;
  const newNodes   = changesJson.newNodes?.length   || 0;
  const modified   = changesJson.modifiedNodes?.length || 0;
  const newEdges   = changesJson.newEdges?.length   || 0;
  const totalFields = (changesJson.modifiedNodes || []).reduce((s, m) => s + (m.addFields?.length || 0), 0);
  const items = [
    newNodes   && `${newNodes} new ${newNodes === 1 ? 'entity' : 'entities'}`,
    totalFields && `${totalFields} field${totalFields > 1 ? 's' : ''} added`,
    newEdges   && `${newEdges} relationship${newEdges > 1 ? 's' : ''}`,
  ].filter(Boolean);
  if (!items.length) return null;

  return (
    <div className="mt-2">
      <button onClick={() => setOpen(v => !v)}
        className="flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:underline">
        {open ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
        {items.join(' · ')}
      </button>
      {open && (
        <ul className="mt-1 space-y-0.5 pl-3 border-l-2 border-blue-200 dark:border-blue-700">
          {(changesJson.newNodes || []).map((n, i) => (
            <li key={i} className="text-xs text-gray-600 dark:text-gray-400">
              + {n.name} <span className="opacity-60">({n.type})</span>
            </li>
          ))}
          {(changesJson.modifiedNodes || []).map((m, i) => (
            (m.addFields || []).map((f, j) => (
              <li key={`${i}-${j}`} className="text-xs text-gray-600 dark:text-gray-400">
                + field: {f.name} <span className="opacity-60">({f.type})</span>
              </li>
            ))
          ))}
          {(changesJson.newEdges || []).map((e, i) => (
            <li key={i} className="text-xs text-gray-600 dark:text-gray-400">
              {e.sourceNodeName} → {e.targetNodeName} <span className="opacity-60">({e.cardinality})</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function AiAssistantModal({ isOpen, onClose }) {
  const { nodes, edges, modelType, currentModel, applyAiChanges } = useFlowStore();

  const [view, setView]             = useState('loading'); // 'loading' | 'config' | 'chat'
  const [settings, setSettings]     = useState(null);
  const [history, setHistory]       = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping]     = useState(false);
  const [includeContext, setIncludeContext] = useState(true);

  const chatEndRef = useRef(null);
  const textareaRef = useRef(null);

  const { isOpen: confirmOpen, config: confirmConfig, confirm, cancel: cancelConfirm } = useConfirm();

  const scrollToBottom = useCallback(() => {
    setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
  }, []);

  // Load settings and history on open
  useEffect(() => {
    if (!isOpen) return;
    setView('loading');
    setHistory([]);

    const load = async () => {
      try {
        const [settingsRes, historyRes] = await Promise.all([
          aiApi.getSettings(),
          currentModel?._id ? aiApi.getHistory(currentModel._id) : Promise.resolve({ data: { messages: [] } }),
        ]);
        const s = settingsRes?.data !== undefined ? settingsRes.data : settingsRes;
        console.log('[AI Assistant] Loaded settings from API:', s);
        setSettings(s);
        const msgs = historyRes?.data?.messages || historyRes?.messages || [];
        setHistory(msgs);
        setView(s?.hasCredentials ? 'chat' : 'config');
      } catch (err) {
        console.error('[AI Assistant] Failed to load settings:', err);
        setView('config');
      }
    };
    load();
  }, [isOpen, currentModel?._id]);

  useEffect(() => {
    if (view === 'chat') scrollToBottom();
  }, [history, view, scrollToBottom]);

  const handleSend = async () => {
    const msg = inputMessage.trim();
    if (!msg || isTyping) return;
    if (!currentModel?._id) {
      toast.error('Save the model first before using the AI assistant.');
      return;
    }
    setInputMessage('');
    setIsTyping(true);

    const tempUserMsg = {
      role: 'user',
      displayContent: msg,
      timestamp: new Date().toISOString(),
      _local: true,
    };
    setHistory(prev => [...prev, tempUserMsg]);
    scrollToBottom();

    try {
      const contextSchema = includeContext ? buildContextSchema(nodes, edges) : { nodes: [], edges: [] };
      const res = await aiApi.complete({
        modelId:    currentModel._id,
        modelType:  modelType || 'relational',
        userMessage: msg,
        contextSchema,
      });

      const assistantMsg = {
        index:          res.messageIndex,
        role:           'assistant',
        displayContent: res.data?.summary || 'Changes proposed.',
        changesJson:    res.data,
        appliedToModel: false,
        timestamp:      new Date().toISOString(),
        _local:         true,
      };

      setHistory(prev => {
        // Remove temporary user message; backend already persisted both
        const withoutTemp = prev.filter(m => !m._local);
        return [
          ...withoutTemp,
          { role: 'user', displayContent: msg, timestamp: tempUserMsg.timestamp },
          assistantMsg,
        ];
      });
    } catch (err) {
      setHistory(prev => [
        ...prev.filter(m => !m._local),
        { role: 'user', displayContent: msg, timestamp: tempUserMsg.timestamp },
        { role: 'error', displayContent: err.message || 'An error occurred.', timestamp: new Date().toISOString() },
      ]);
    } finally {
      setIsTyping(false);
      scrollToBottom();
    }
  };

  const handleApply = async (msg, localIndex) => {
    try {
      const parsed = parseAiResponse(msg.changesJson, nodes);
      applyAiChanges(parsed);
      toast.success('Changes applied to the model.');

      // Mark applied locally
      setHistory(prev => prev.map((m, i) =>
        i === localIndex ? { ...m, appliedToModel: true } : m
      ));

      // Mark applied in backend (non-blocking)
      if (msg.index != null && currentModel?._id) {
        aiApi.markApplied(currentModel._id, msg.index).catch(() => {});
      }
    } catch (err) {
      toast.error(err.message || 'Error applying changes.');
    }
  };

  const handleClearHistory = () => {
    confirm({
      title: 'Clear conversation history',
      message: 'All messages for this model will be permanently deleted. This action cannot be undone.',
      confirmText: 'Clear history',
      cancelText: 'Cancel',
      type: 'danger',
      onConfirm: async () => {
        try {
          if (currentModel?._id) await aiApi.clearHistory(currentModel._id);
          setHistory([]);
          toast.success('Conversation history cleared.');
        } catch {
          toast.error('Error clearing history.');
        }
      },
    });
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!isOpen) return null;

  const providerLabel = PROVIDERS.find(p => p.value === settings?.provider)?.label || settings?.provider || '';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-2xl mx-4 flex flex-col"
        style={{ height: '85vh', maxHeight: '720px' }}>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <div className="flex items-center gap-2">
            <Bot size={20} className="text-blue-600 dark:text-blue-400" />
            <h2 className="text-base font-semibold text-gray-900 dark:text-white">AI Assistant</h2>
            {settings?.hasCredentials && view === 'chat' && (
              <span className="text-xs px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full">
                {providerLabel} · {settings.model}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1">
            {view === 'chat' && settings?.hasCredentials && (
              <>
                <button onClick={() => setView('config')}
                  title="Change provider settings"
                  className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors text-gray-500 dark:text-gray-400">
                  <Settings size={16} />
                </button>
                {history.length > 0 && (
                  <button onClick={handleClearHistory}
                    title="Clear conversation"
                    className="p-1.5 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400">
                    <Trash2 size={16} />
                  </button>
                )}
              </>
            )}
            <button onClick={onClose}
              className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors text-gray-500 dark:text-gray-400">
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-hidden flex flex-col min-h-0">

          {/* Loading */}
          {view === 'loading' && (
            <div className="flex-1 flex items-center justify-center">
              <Loader2 size={28} className="animate-spin text-blue-500" />
            </div>
          )}

          {/* Config Form */}
          {view === 'config' && (
            <div className="flex-1 overflow-y-auto p-5">
              <ConfigForm
                currentSettings={settings}
                onSaved={(s) => { setSettings(s); setView('chat'); }}
                onCancel={settings?.hasCredentials ? () => setView('chat') : null}
              />
            </div>
          )}

          {/* Chat View */}
          {view === 'chat' && (
            <>
              {/* Message list */}
              <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
                {history.length === 0 && !isTyping && (
                  <div className="flex flex-col items-center justify-center h-full text-center py-8 text-gray-400 dark:text-gray-500">
                    <Bot size={40} className="mb-3 opacity-30" />
                    <p className="text-sm">Describe what you want to add or change in the model.</p>
                    <p className="text-xs mt-1">Example: "Add a Product entity with name, price and stock fields"</p>
                  </div>
                )}

                {history.map((msg, i) => {
                  if (msg.role === 'user') {
                    return (
                      <div key={i} className="flex justify-end">
                        <div className="max-w-[80%] px-4 py-2.5 bg-blue-600 text-white rounded-2xl rounded-br-sm text-sm">
                          {msg.displayContent}
                        </div>
                      </div>
                    );
                  }

                  if (msg.role === 'error') {
                    return (
                      <div key={i} className="flex justify-start">
                        <div className="max-w-[85%] px-4 py-2.5 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-2xl rounded-bl-sm">
                          <div className="flex items-center gap-2 text-red-700 dark:text-red-400 text-sm">
                            <AlertCircle size={14} className="flex-shrink-0" />
                            {msg.displayContent}
                          </div>
                        </div>
                      </div>
                    );
                  }

                  if (msg.role === 'assistant') {
                    return (
                      <div key={i} className="flex justify-start">
                        <div className="max-w-[85%] px-4 py-3 bg-gray-100 dark:bg-gray-700 rounded-2xl rounded-bl-sm">
                          <div className="flex items-start gap-2">
                            <Bot size={14} className="flex-shrink-0 mt-0.5 text-blue-500" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-gray-800 dark:text-gray-200">{msg.displayContent}</p>
                              {msg.changesJson && <ChangeSummary changesJson={msg.changesJson} />}
                              <div className="mt-2">
                                {msg.appliedToModel ? (
                                  <span className="inline-flex items-center gap-1 text-xs text-green-600 dark:text-green-400">
                                    <CheckCircle size={12} /> Applied
                                  </span>
                                ) : (
                                  msg.changesJson && (
                                    <button
                                      onClick={() => handleApply(msg, i)}
                                      className="text-xs px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded-full transition-colors"
                                    >
                                      Apply changes
                                    </button>
                                  )
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  }
                  return null;
                })}

                {isTyping && (
                  <div className="flex justify-start">
                    <div className="px-4 py-3 bg-gray-100 dark:bg-gray-700 rounded-2xl rounded-bl-sm">
                      <div className="flex items-center gap-1">
                        <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                        <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                        <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                    </div>
                  </div>
                )}

                <div ref={chatEndRef} />
              </div>

              {/* Input area */}
              <div className="px-4 pb-4 pt-2 border-t border-gray-100 dark:border-gray-700 flex-shrink-0">
                <div className="flex items-center gap-2 mb-2">
                  <label className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={includeContext}
                      onChange={e => setIncludeContext(e.target.checked)}
                      className="w-3.5 h-3.5 rounded"
                    />
                    Include current model as context
                    <span className="text-gray-400">
                      ({nodes.filter(n => n.type !== 'text').length} entities, {edges.length} relationships)
                    </span>
                  </label>
                </div>
                <div className="flex gap-2">
                  <textarea
                    ref={textareaRef}
                    value={inputMessage}
                    onChange={e => setInputMessage(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Describe what you want to add or change... (Enter to send, Shift+Enter for new line)"
                    rows={2}
                    disabled={isTyping}
                    className="flex-1 resize-none px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                  />
                  <button
                    onClick={handleSend}
                    disabled={!inputMessage.trim() || isTyping}
                    className="flex-shrink-0 w-10 h-10 self-end flex items-center justify-center bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-xl transition-colors"
                    title="Send (Enter)"
                  >
                    {isTyping
                      ? <Loader2 size={16} className="animate-spin" />
                      : <Send size={16} />
                    }
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <ConfirmDialog
        isOpen={confirmOpen}
        title={confirmConfig.title}
        message={confirmConfig.message}
        confirmText={confirmConfig.confirmText}
        cancelText={confirmConfig.cancelText}
        type={confirmConfig.type}
        onConfirm={confirmConfig.onConfirm}
        onCancel={cancelConfirm}
      />
    </div>
  );
}
